// Per-conversation legal-safe indicator — a SPLIT shield with two independent
// halves:
//   LEFT  half → ESTIMATE track (pre-service authorization of the work)
//   RIGHT half → INVOICE  track (post-service signature on the final bill)
// Each half is GREEN when that track is fully covered by a legal doc, EMPTY/
// hollow (just an outline) when at least one selected item is missing a doc, and
// GREY when there's nothing to authorize on that track. When BOTH tracks are
// N/A the whole badge collapses to a neutral dash.
//
// Reads `quote_legal_safe_estimate` / `quote_legal_safe_invoice` from the
// conversation (falling back to the legacy single `quote_legal_safe` for the
// estimate half). Callers may pass `safeOverride` (estimate) /
// `invoiceSafeOverride` to feed locally-computed state so the badge updates
// instantly while editing, before the conv-list refresh lands.
//
// When `conversationId` is passed the badge becomes a button that navigates to
// /shop/conversations/{id}?tab=quote. Click stops propagation so a parent
// clickable doesn't double-fire.

import { Tooltip, UnstyledButton } from '@mantine/core';
import { useId } from 'react';
import { useNavigate } from 'react-router-dom';
import type { ConversationResponse } from '../types';

const GREEN = '#22C55E';
const GREY = '#CBD5E1';
const OUTLINE = '#64748B'; // muted slate — visible on dark + light for the hollow (missing) half

// Classic shield outline in a 24x24 box, split vertically at x=12.
const SHIELD_PATH = 'M12 2 L20 5 V11 C20 16.5 16.5 20.5 12 22 C7.5 20.5 4 16.5 4 11 V5 Z';

type Safe = boolean | null | undefined;

type Props = {
  conv?: Pick<
    ConversationResponse,
    'quote_legal_safe' | 'quote_legal_safe_estimate' | 'quote_legal_safe_invoice'
  > | null;
  size?: number;
  // Optional — when provided, badge becomes a button that navigates to the
  // conversation's Quote tab on click. Omit to render as a read-only indicator.
  conversationId?: string | number | null;
  // Live local overrides (estimate / invoice). undefined = fall back to conv.
  safeOverride?: Safe;
  invoiceSafeOverride?: Safe;
};

function trackColor(s: Safe): string {
  // Covered = solid green. Missing (false) = empty/hollow (transparent) so it
  // reads as "not done" — NOT a gold/amber shield that looks earned. Nothing to
  // authorize (null) = muted grey.
  return s === true ? GREEN : s === false ? 'transparent' : GREY;
}

function trackWord(s: Safe): string {
  return s === true ? 'covered' : s === false ? 'missing a legal doc' : 'nothing to authorize';
}

export function AuthorizedDot({
  conv,
  size = 13,
  conversationId,
  safeOverride,
  invoiceSafeOverride,
}: Props) {
  const navigate = useNavigate();
  const clipId = useId();

  const estimate: Safe =
    safeOverride !== undefined
      ? safeOverride
      : (conv?.quote_legal_safe_estimate ?? conv?.quote_legal_safe ?? null);
  const invoice: Safe =
    invoiceSafeOverride !== undefined ? invoiceSafeOverride : (conv?.quote_legal_safe_invoice ?? null);

  const clickable = conversationId !== undefined && conversationId !== null;
  // Nothing to authorize on EITHER track (no selected/quoted items) → the shield
  // (which signals legal-doc coverage) is meaningless. Show a neutral muted dash
  // instead; reserve the shield for real covered/missing state.
  const nothingToAuthorize = estimate === null && invoice === null;
  const label = nothingToAuthorize
    ? 'Nothing to authorize' + (clickable ? ' — open Quote' : '')
    : `Estimate: ${trackWord(estimate)} · Invoice: ${trackWord(invoice)}` +
      (clickable ? ' — open Quote' : '');

  const px = size + 4;
  const inner = nothingToAuthorize ? (
    <span
      aria-hidden="true"
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: px,
        height: px,
        color: GREY,
        fontSize: size,
        fontWeight: 600,
        lineHeight: 1,
      }}
    >
      —
    </span>
  ) : (
    <svg width={px} height={px} viewBox="0 0 24 24" aria-hidden="true">
      <defs>
        <clipPath id={clipId}>
          <path d={SHIELD_PATH} />
        </clipPath>
      </defs>
      <g clipPath={`url(#${clipId})`}>
        <rect x={0} y={0} width={12} height={24} fill={trackColor(estimate)} />
        <rect x={12} y={0} width={12} height={24} fill={trackColor(invoice)} />
      </g>
      {/* center divider + crisp shield border (border stays visible so a hollow
          "missing" half still reads as an outlined shield, not blank space) */}
      <line x1={12} y1={3} x2={12} y2={21} stroke={OUTLINE} strokeWidth={0.9} opacity={0.55} />
      <path d={SHIELD_PATH} fill="none" stroke={OUTLINE} strokeOpacity={0.9} strokeWidth={1.4} />
    </svg>
  );

  if (!clickable) {
    return (
      <Tooltip label={label} withinPortal>
        <span
          style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: 2, cursor: 'help' }}
          role="img"
          aria-label={label}
        >
          {inner}
        </span>
      </Tooltip>
    );
  }

  return (
    <Tooltip label={label} withinPortal>
      <UnstyledButton
        onClick={(e) => {
          e.stopPropagation();
          navigate(`/shop/conversations/${conversationId}?tab=quote`);
        }}
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          marginTop: 2,
          borderRadius: 4,
          cursor: 'pointer',
        }}
        aria-label={label}
      >
        {inner}
      </UnstyledButton>
    </Tooltip>
  );
}
